import { useMemo } from 'react';
import CustomStore from 'devextreme/data/custom_store';
import { getUserAudit } from '../../../api/administration';
import UserAuditGridConfig from './UserAuditGridConfig';

/**
 * Модуль: источник данных для грида "Аудит пользователей".
 *
 * Задача модуля:
 * - инкапсулировать всю логику общения DevExtreme `DataGrid` с REST API user-audit;
 * - спрятать детали формата `loadOptions` (skip, take, sort, filter) и формата тела запроса к бэкенду;
 * - обеспечить единое место, где настраивается сортировка, фильтрация и трансформация данных.
 *
 * Используемые абстракции:
 * - `UserAuditStore` — класс, который знает, КАК загрузить данные аудита;
 * - `UserAuditFilterParser` — класс, который умеет разбирать devextreme‑фильтры в вид, удобный для API;
 * - `UserAuditResponseMapper` — класс, который превращает ответ API в данные для грида.
 *
 * Почему `CustomStore`:
 * - DevExtreme `DataGrid` не делает запросы сам по себе, он работает через dataSource;
 * - `CustomStore` — это адаптер (обёртка), в который мы передаём свою функцию `load`,
 *   а грид уже сам вызывает её при постраничной загрузке, сортировке и фильтрации.
 *
 * Почему `useMemo` вокруг стора:
 * - создание `CustomStore` — "тяжёлая" операция, и делать её при каждом рендере нет смысла;
 * - `useMemo` гарантирует, что мы создадим новый экземпляр стора ТОЛЬКО,
 *   когда поменяются входные параметры (период отчёта или функция `setLoading`).
 *
 * Зачем `setLoading(true/false)`:
 * - это обычный React‑стейт, который управляет показом `LoadPanel` на странице;
 * - мы явно помечаем момент старта/окончания загрузки, а визуальный компонент
 *   уже реагирует на изменение этого состояния.
 */

/** Операторы фильтра DevExtreme, которые мы умеем превращать в параметры API. Равенство приходит как "eq" или "=". */
const FILTER_OPERATORS = ['=', 'eq', 'contains', 'startswith', 'endswith'];

/**
 * Нормализует значение фильтра для поля "success" в boolean (API ждёт true/false, а не "Да"/"Нет").
 *
 * @param {string} field - имя поля API.
 * @param {any} value - значение из фильтра DevExtreme.
 * @returns {any}
 */
function normalizeFilterValue(field, value) {
    if (field !== 'success') return value;
    if (value === true || value === 'Да' || value === 'да' || value === '1') return true;
    if (value === false || value === 'Нет' || value === 'нет' || value === '0') return false;
    return value;
}

/**
 * Класс, отвечающий за разбор devextreme‑фильтра
 * (`loadOptions.filter`) в компактный объект параметров API.
 */
class UserAuditFilterParser {
    /**
     * Превращает дерево фильтров DevExtreme в объект:
     * `{ dat?, fio?, email?, ndog?, entity?, success? }`.
     * Поддерживает операторы: =, eq, contains, startswith, endswith (DevExtreme часто шлёт "eq" для равенства).
     *
     * @param {any} rawFilter - значение `loadOptions.filter` из DevExtreme DataGrid.
     * @returns {Object.<string, any>}
     */
    static fromDevExtreme(rawFilter) {
        const out = {};
        if (!rawFilter || !Array.isArray(rawFilter)) {
            return out;
        }

        // DevExtreme строит фильтр в виде вложенных массивов.
        // Здесь мы рекурсивно обходим это дерево и вытаскиваем только "простые" условия.
        const walk = (node) => {
            if (!Array.isArray(node)) return;

            // Простой фильтр: [field, operator, value] — поддерживаем и "=", и "eq" (часто для булевых колонок).
            if (node.length === 3 && FILTER_OPERATORS.includes(node[1])) {
                const field = UserAuditGridConfig.getApiParam(node[0]);
                if (UserAuditGridConfig.API_FIELDS.includes(field)) {
                    out[field] = normalizeFilterValue(field, node[2]);
                }
                return;
            }

            // Групповой фильтр: ['and', expr1, expr2, ...] или ['or', expr1, expr2, ...]
            if (node.length >= 2 && (node[0] === 'and' || node[0] === 'or')) {
                node.slice(1).forEach(walk);
                return;
            }

            // Любой другой вариант считаем массивом подвыражений.
            node.forEach(walk);
        };

        walk(rawFilter);
        return out;
    }
}

/**
 * Класс, который знает, как превратить ответ API
 * в формат, подходящий для DataGrid (data + totalCount).
 */
class UserAuditResponseMapper {
    /**
     * @param {Array<Object>} apiList - "сырые" записи из бэкенда.
     * @param {number} skip - смещение (offset) для корректного построения id.
     * @param {Object} pagination - объект пагинации из ответа API.
     * @returns {{ data: Array<Object>, totalCount: number }}
     */
    static toGridData(apiList, skip, pagination) {
        const list = apiList ?? [];
        const total = pagination?.total ?? list.length;

        // В гриде нужен стабильный ключ `id`. Собираем его детерминированно:
        // номер строки в выборке + часть значимых полей.
        const data = list.map((row, index) => ({
            ...row,
            id: `${skip + index}-${row.dat}-${row.email ?? ''}-${row.entity ?? ''}`,
        }));

        return { data, totalCount: total };
    }
}

/**
 * Класс, инкапсулирующий всю бизнес‑логику загрузки аудита пользователей.
 * Снаружи предоставляет только один метод `createCustomStore()`,
 * который уже можно напрямую отдать в `DataGrid` как `dataSource`.
 */
class UserAuditStore {
    /**
     * @param {Object} params
     * @param {Date}   params.periodStart - начало периода аудита.
     * @param {Date}   params.periodEnd   - конец периода аудита.
     * @param {Function} params.setLoading - React‑сеттер, управляющий индикатором загрузки.
     */
    constructor({ periodStart, periodEnd, setLoading }) {
        this.periodStart = periodStart;
        this.periodEnd = periodEnd;
        this.setLoading = setLoading;
    }

    /**
     * Публичная точка входа: создаёт `CustomStore`, который понимает DataGrid.
     *
     * Важно:
     * - сам класс ничего не знает про React‑хуки;
     * - он чисто "сервис" уровня бизнес‑логики + адаптер под DevExtreme.
     */
    createCustomStore() {
        return new CustomStore({
            key: 'id',
            load: this.#load.bind(this),
        });
    }

    /**
     * Реализация протокола DevExtreme CustomStore.load.
     *
     * @param {Object} loadOptions - объект, который DevExtreme передаёт в `CustomStore.load`:
     *   - skip / take — постраничная загрузка;
     *   - sort       — массив правил сортировки;
     *   - filter     — дерево фильтров.
     */
    async #load(loadOptions) {
        const { setLoading, periodStart, periodEnd } = this;

        try {
            // Сообщаем UI, что начинается запрос на сервер.
            setLoading(true);

            const skip = loadOptions.skip ?? 0;
            const take = loadOptions.take ?? 50;

            // --- Сортировка ---
            let sortBy = 'dat';
            let sortOrder = 'DESC';

            // DevExtreme может прислать несколько правил сортировки, но здесь
            // бизнес‑требование — учитывать только первое (основное).
            if (loadOptions.sort?.length) {
                const sortRule = loadOptions.sort[0];
                sortBy = UserAuditGridConfig.getApiParam(sortRule.selector);
                sortOrder = sortRule.desc ? 'DESC' : 'ASC';
            }

            // --- Фильтры ---
            const filters = UserAuditFilterParser.fromDevExtreme(loadOptions.filter);

            // --- Сборка тела запроса к API ---
            const body = {
                d1: periodStart,
                d2: periodEnd,
                limit: take,
                offset: skip,
                sortBy,
                sortOrder,
                email: filters.email ?? null,
                fio: filters.fio ?? null,
                ndog: filters.ndog ?? null,
                entity: filters.entity ?? null,
                success: filters.success ?? null,
            };

            const response = await getUserAudit(body);

            // Запрос на этом этапе завершён — прячем индикатор.
            setLoading(false);

            if (!response?.isOk) {
                // Ошибку дополнительно логируем, чтобы не терять информацию при отладке.
                console.error('User audit API returned error state:', response);
                return { data: [], totalCount: 0 };
            }

            return UserAuditResponseMapper.toGridData(response.data, skip, response.pagination);
        } catch (error) {
            // Любая техническая ошибка (сетевое исключение и т.п.).
            console.error('User audit load error:', error);
            setLoading(false);
            return { data: [], totalCount: 0 };
        }
    }
}

/**
 * React‑хук, который адаптирует `UserAuditStore` под Hooks‑мир.
 *
 * В результате хук возвращает уже готовый `CustomStore`,
 * который можно напрямую передать в DevExtreme `DataGrid` как `dataSource`.
 *
 * @param {Date} periodStart - начало периода.
 * @param {Date} periodEnd   - конец периода.
 * @param {Function} setLoading - React‑сеттер для управления индикатором загрузки.
 */
export function useUserAuditStore(periodStart, periodEnd, setLoading) {
    // Важно: `useMemo` гарантирует, что `CustomStore` создаётся
    // только при изменении входных параметров периода / функции `setLoading`.
    // Это избавляет от лишних пересозданий стора и повторных запросов.
    return useMemo(() => {
        const store = new UserAuditStore({ periodStart, periodEnd, setLoading });
        return store.createCustomStore();
    }, [periodStart, periodEnd, setLoading]);
}

